import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAuthStore } from '../../store/authStore';
import { clientPortalAPI } from '../../api';
import { getClientAreaSignInPath } from '../../lib/authFailureRedirect';
import { Loader2 } from 'lucide-react';

export default function ClientOnboarding() {
  const navigate = useNavigate();
  const updateUser = useAuthStore((s) => s.updateUser);
  const logout = useAuthStore((s) => s.logout);

  const { data, isLoading, error } = useQuery({
    queryKey: ['portal-me', 'onboarding'],
    queryFn: () => clientPortalAPI.getMe(),
  });

  const [step, setStep] = useState(1);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [contact, setContact] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    mobile: '',
  });
  const [clientAddr, setClientAddr] = useState({
    companyName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    county: '',
    postcode: '',
    country: 'GB',
  });
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!data) return;
    setContact((c) => ({
      ...c,
      firstName: data.contact?.firstName ?? '',
      lastName: data.contact?.lastName ?? '',
      phone: data.contact?.phone ?? '',
      mobile: data.contact?.mobile ?? '',
    }));
    setClientAddr({
      companyName: data.client?.companyName ?? '',
      addressLine1: data.client?.addressLine1 ?? '',
      addressLine2: data.client?.addressLine2 ?? '',
      city: data.client?.city ?? '',
      county: data.client?.county ?? '',
      postcode: data.client?.postcode ?? '',
      country: data.client?.country || 'GB',
    });
    if (data.user?.onboardingCompletedAt) {
      navigate('/client/dashboard', { replace: true });
    }
  }, [data, navigate]);

  const passwordIsSet = data?.passwordIsSet;
  const primary = '#3B82F6';

  const handleComplete = async () => {
    setSubmitError('');
    if (!passwordIsSet) {
      if (password.length < 8) {
        setSubmitError('Password must be at least 8 characters.');
        return;
      }
      if (password !== passwordConfirm) {
        setSubmitError('Passwords do not match.');
        return;
      }
    } else if (password) {
      if (password.length < 8) {
        setSubmitError('New password must be at least 8 characters.');
        return;
      }
      if (password !== passwordConfirm) {
        setSubmitError('Passwords do not match.');
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await clientPortalAPI.completeOnboarding({
        password:
          !passwordIsSet ? password : password || undefined,
        passwordConfirm:
          !passwordIsSet ? passwordConfirm : password ? passwordConfirm : undefined,
        firstName: contact.firstName,
        lastName: contact.lastName,
        phone: contact.phone,
        mobile: contact.mobile,
        client: clientAddr,
      });
      updateUser(res.user);
      navigate('/client/dashboard', { replace: true });
    } catch (err) {
      setSubmitError(err.message || 'Could not save your details.');
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-gray-50 dark:bg-gray-900">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
        <p className="text-sm text-gray-600 dark:text-gray-400">Loading your profile…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4 bg-gray-50 dark:bg-gray-900">
        <p className="text-gray-700 dark:text-gray-300 text-center">{error.message || 'Something went wrong.'}</p>
        <button
          type="button"
          onClick={() => {
            const slugHint = useAuthStore.getState().user?.portalSlug;
            const path = getClientAreaSignInPath(
              typeof slugHint === 'string' ? slugHint : undefined
            );
            logout();
            navigate(path);
          }}
          className="px-4 py-2 rounded-md bg-gray-200 dark:bg-gray-700 text-sm"
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-10 px-4">
      <div className="max-w-lg mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Welcome</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Step {step} of 2 — set up your client portal
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6 space-y-6">
          {submitError && (
            <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/25 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-200">
              {submitError}
            </div>
          )}

          {step === 1 && (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {passwordIsSet
                  ? 'Your password is already set. You can enter a new one below to replace it, or continue to confirm your details.'
                  : 'Choose a password you will use with your email to sign in to this portal.'}
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {passwordIsSet ? 'New password (optional)' : 'Password'}
                </label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Confirm password
                </label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!passwordIsSet) {
                    if (password.length < 8 || password !== passwordConfirm) {
                      setSubmitError('Enter a matching password of at least 8 characters.');
                      return;
                    }
                  } else if (password && (password.length < 8 || password !== passwordConfirm)) {
                    setSubmitError('New passwords must match and be at least 8 characters.');
                    return;
                  }
                  setSubmitError('');
                  setStep(2);
                }}
                className="w-full py-2.5 rounded-md text-white text-sm font-medium"
                style={{ backgroundColor: primary }}
              >
                Continue
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Check your details and billing address. Your supplier uses this on invoices and statements.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">First name</label>
                  <input
                    value={contact.firstName}
                    onChange={(e) => setContact((c) => ({ ...c, firstName: e.target.value }))}
                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Last name</label>
                  <input
                    value={contact.lastName}
                    onChange={(e) => setContact((c) => ({ ...c, lastName: e.target.value }))}
                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Phone</label>
                <input
                  value={contact.phone}
                  onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Mobile</label>
                <input
                  value={contact.mobile}
                  onChange={(e) => setContact((c) => ({ ...c, mobile: e.target.value }))}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Company name</label>
                <input
                  value={clientAddr.companyName}
                  onChange={(e) => setClientAddr((c) => ({ ...c, companyName: e.target.value }))}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Address line 1</label>
                <input
                  value={clientAddr.addressLine1}
                  onChange={(e) => setClientAddr((c) => ({ ...c, addressLine1: e.target.value }))}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Address line 2</label>
                <input
                  value={clientAddr.addressLine2}
                  onChange={(e) => setClientAddr((c) => ({ ...c, addressLine2: e.target.value }))}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">City</label>
                  <input
                    value={clientAddr.city}
                    onChange={(e) => setClientAddr((c) => ({ ...c, city: e.target.value }))}
                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">County</label>
                  <input
                    value={clientAddr.county}
                    onChange={(e) => setClientAddr((c) => ({ ...c, county: e.target.value }))}
                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Postcode</label>
                  <input
                    value={clientAddr.postcode}
                    onChange={(e) => setClientAddr((c) => ({ ...c, postcode: e.target.value }))}
                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Country</label>
                  <input
                  value={clientAddr.country}
                  onChange={(e) => setClientAddr((c) => ({ ...c, country: e.target.value }))}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 py-2.5 rounded-md border border-gray-300 dark:border-gray-600 text-sm"
                >
                  Back
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={handleComplete}
                  className="flex-1 py-2.5 rounded-md text-white text-sm font-medium disabled:opacity-50"
                  style={{ backgroundColor: primary }}
                >
                  {submitting ? 'Saving…' : 'Finish'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
